// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a still of an HTML page as its script builds it, for cards. Cards don't run a page's
// script in place: a sandboxed frame can share Konvo's main thread (WebKit), so a busy page would
// stall the app for everyone who scrolls past it. Instead the script runs once in a Web Worker
// against a DOM stand-in (linkedom: no layout or painting), started from a hidden frame with no
// network or storage, and the document it leaves comes back as static HTML. The worker gets a
// short budget; a page that loops, waits or fails just keeps its static markup. Stills are kept
// per page, so a card that scrolls back, or a chat opened again, doesn't run it twice.

import {DOCUMENT_PREVIEW_MAX_BYTES} from '@app/features/document_preview/DocumentPreviewUtils';
import {createContext} from 'react';

/** Whether cards below may run their pages' scripts for a still (Agents: pages agents wrote). */
export const PageSnapshotsContext = createContext(false);

/** The stills frame's and its worker's policy: their own script and the worker, nothing else. */
const SNAPSHOT_POLICY =
	"default-src 'none'; script-src 'unsafe-inline' blob:; worker-src blob:; connect-src 'none'; img-src 'none'; style-src 'none'; font-src 'none'; media-src 'none'; frame-src 'none'; object-src 'none'; form-action 'none'; base-uri 'none'";
/** How long a page's script may take, worker start included. */
const SNAPSHOT_BUDGET_MS = 2000;
/** How long after load the page may keep building (timers, deferred renders). */
const SNAPSHOT_SETTLE_MS = 400;
/** The longest still a card takes (as long as the pages cards show); a bigger one keeps the page's markup. */
const SNAPSHOT_MAX_LENGTH = DOCUMENT_PREVIEW_MAX_BYTES;
/** A frame that never answers (it can't hang: only the worker runs the page). */
const HOST_TIMEOUT_MS = 10_000;
const KEPT = 40;

/** Whether the page has script that could change what it shows. */
export function pageHasScript(html: string): boolean {
	return /<script\b/i.test(html);
}

/**
 * linkedom's worker build is a module; a classic worker runs pages' scripts with importScripts (the
 * policy forbids eval), so its exports become a global the runner picks up.
 */
export function classicDom(moduleSource: string): string | null {
	const list = /^export\s*\{([^}]*)\};?\s*$/m.exec(moduleSource);
	if (!list) return null;
	const entries = list[1]!
		.split(',')
		.map((part) => part.trim())
		.filter(Boolean)
		.map((part) => {
			const [local, exported = local] = part.split(/\s+as\s+/);
			return `${JSON.stringify(exported)}:${local}`;
		});
	const body = moduleSource.replace(list[0], '').replace(/^export\s+(?=(const|let|var|function|class)\s)/gm, '');
	if (/^\s*(export|import)\s/m.test(body)) return null;
	// In a function of its own, so its top-level names (`add`, `customElements`) don't shadow the page's.
	return `(function(){\n${body}\nself.__konvoDom={${entries.join(',')}};\n})();\n`;
}

/**
 * Runs in the worker after linkedom: turns its global into the page's window, closes everything
 * that reaches outside, runs the page's scripts in order (each like a <script>, sharing globals),
 * fires DOMContentLoaded and load, lets timers settle, and sends back the document without its
 * scripts.
 */
export function snapshotRunner(): string {
	return `;(function(){
var dom=self.__konvoDom;delete self.__konvoDom;
var load=self.importScripts.bind(self),send=self.postMessage.bind(self),later=self.setTimeout.bind(self),NativeEvent=self.Event;
var objectUrl=URL.createObjectURL.bind(URL),revokeUrl=URL.revokeObjectURL.bind(URL),NativeBlob=Blob;
var noop=function(){},errors=[];
function failed(error){if(errors.length<5)errors.push(String(error&&error.message||error).slice(0,300));}
function close(target,name){
try{Object.defineProperty(target,name,{value:undefined,configurable:true,writable:true});}catch(error){}
}
function blocked(target,name){
try{Object.defineProperty(target,name,{configurable:true,get:function(){throw new DOMException('Blocked','SecurityError');}});}catch(error){}
}
function run(code){
if(!code||!/\\S/.test(code))return;
// Chromium doesn't hold a blob worker's import() to the frame's policy, so the page may not use
// the keyword: escaped, it reads the same in strings and regular expressions but can't be called.
// Code can't be made any other way (eval, Function and string timers are blocked; importScripts is gone).
code=code.replace(/\\bimport\\b/g,'\\\\u0069mport');
var url=objectUrl(new NativeBlob([code],{type:'text/javascript'}));
try{load(url);}catch(error){failed(error);}finally{revokeUrl(url);}
}
self.onmessage=function(event){
self.onmessage=null;
var data=event.data||{};
try{build(String(data.html||''),data.width>0?data.width:1200,data.settle>=0?data.settle:400,data.max>0?data.max:2097152);}
catch(error){failed(error);send({html:null,reason:'error',errors:errors});}
};
function build(html,width,settle,max){
var parsed=dom.parseHTML(html),win=parsed.window,doc=parsed.document;
// Nothing leaves the page: no network, storage, other workers, or messages but the still.
['fetch','XMLHttpRequest','WebSocket','WebSocketStream','EventSource','WebTransport','FontFace','fonts','indexedDB','caches','BroadcastChannel','Worker','SharedWorker','importScripts','postMessage','close','requestFileSystem','webkitRequestFileSystem'].forEach(function(name){
close(self,name);
for(var proto=Object.getPrototypeOf(self);proto&&proto!==Object.prototype;proto=Object.getPrototypeOf(proto))
if(Object.prototype.hasOwnProperty.call(proto,name))try{delete proto[name];}catch(error){}
});
blocked(self,'localStorage');blocked(self,'sessionStorage');
// The page's window: the worker's own global, with linkedom's document and classes.
Object.keys(dom).forEach(function(name){
if(!/^[A-Z]/.test(name)||name==='Facades'||name==='HTMLClasses')return;
try{self[name]=dom[name];}catch(error){}
});
var classes=['Image','Audio','Option'];
classes.forEach(function(name){var real=win[name];if(real)try{self[name]=real;}catch(error){}});
var screen={width:width,height:900,availWidth:width,availHeight:900};
var values={document:doc,window:self,parent:self,top:self,frames:self,opener:null,frameElement:null,customElements:win.customElements,
location:{href:'about:srcdoc',protocol:'about:',host:'',hostname:'',pathname:'srcdoc',search:'',hash:'',origin:'null',assign:noop,replace:noop,reload:noop},
history:{length:1,state:null,pushState:noop,replaceState:noop,back:noop,forward:noop,go:noop},
innerWidth:width,innerHeight:900,outerWidth:width,outerHeight:900,devicePixelRatio:1,screen:screen,scrollX:0,scrollY:0,pageXOffset:0,pageYOffset:0,
alert:noop,confirm:function(){return false;},prompt:function(){return null;},print:noop,focus:noop,blur:noop,scrollTo:noop,scrollBy:noop,scroll:noop,open:function(){return null;},
getComputedStyle:function(element){var style=element&&element.style;return style||{getPropertyValue:function(){return '';}};},
matchMedia:function(query){return {matches:false,media:String(query),onchange:null,addListener:noop,removeListener:noop,addEventListener:noop,removeEventListener:noop};},
requestAnimationFrame:function(callback){return setTimeout(function(){callback(performance.now());},16);},
cancelAnimationFrame:function(id){clearTimeout(id);},
requestIdleCallback:function(callback){return setTimeout(function(){callback({didTimeout:false,timeRemaining:function(){return 10;}});},1);},
cancelIdleCallback:function(id){clearTimeout(id);}};
Object.keys(values).forEach(function(name){try{Object.defineProperty(self,name,{value:values[name],configurable:true,writable:true});}catch(error){}});
// Sizes without layout: a width or height the page set in pixels (style or attribute), else the
// nearest one above, else the window's width; heights default to 0.
function pixels(element,name){
if(!element||element.nodeType!==1)return null;
var styled=name==='width'?/(?:^|;)\\s*width\\s*:\\s*([\\d.]+)px/i:/(?:^|;)\\s*height\\s*:\\s*([\\d.]+)px/i;
var match=styled.exec(element.getAttribute('style')||'')||/^\\s*([\\d.]+)(px)?\\s*$/.exec(element.getAttribute(name)||'');
return match?parseFloat(match[1]):null;
}
function widthOf(element){
for(var node=element,depth=0;node&&node.nodeType===1&&depth<60;node=node.parentNode,depth++){var found=pixels(node,'width');if(found!==null)return found;}
return width;
}
function heightOf(element){var found=pixels(element,'height');return found===null?0:found;}
function rect(element){var w=widthOf(element),h=heightOf(element);return {x:0,y:0,top:0,left:0,right:w,bottom:h,width:w,height:h,toJSON:function(){return this;}};}
var elementProto=dom.Element&&dom.Element.prototype;
if(elementProto){
elementProto.getBoundingClientRect=function(){return rect(this);};
elementProto.getClientRects=function(){return [rect(this)];};
if(!elementProto.scrollIntoView)elementProto.scrollIntoView=noop;
}
var proto=dom.HTMLElement&&dom.HTMLElement.prototype;
var sizes={offsetWidth:widthOf,clientWidth:widthOf,scrollWidth:widthOf,offsetHeight:heightOf,clientHeight:heightOf,scrollHeight:heightOf,offsetTop:function(){return 0;},offsetLeft:function(){return 0;}};
Object.keys(sizes).forEach(function(name){
if(proto)try{Object.defineProperty(proto,name,{configurable:true,get:function(){return sizes[name](this);}});}catch(error){}
});
if(proto&&!proto.focus)proto.focus=noop;
// Everything is on screen, at its size, once: observers report that right after they start watching.
function watcher(entry){
return function(callback){
var watched=[],observer=this,live=true;
this.observe=function(target){if(!target||watched.indexOf(target)>=0)return;watched.push(target);later(function(){if(live&&watched.indexOf(target)>=0)try{callback([entry(target)],observer);}catch(error){failed(error);}},0);};
this.unobserve=function(target){var at=watched.indexOf(target);if(at>=0)watched.splice(at,1);};
this.disconnect=function(){live=false;watched=[];};
this.takeRecords=function(){return [];};
};
}
self.IntersectionObserver=watcher(function(target){var box=rect(target);return {target:target,isIntersecting:true,intersectionRatio:1,boundingClientRect:box,intersectionRect:box,rootBounds:{x:0,y:0,top:0,left:0,width:width,height:900,right:width,bottom:900},time:performance.now()};});
self.ResizeObserver=watcher(function(target){var box=rect(target),size=[{inlineSize:box.width,blockSize:box.height}];return {target:target,contentRect:box,borderBoxSize:size,contentBoxSize:size,devicePixelContentBoxSize:size};});
// Markup set inside SVG is SVG (<circle/> closes), as a browser parses it.
var svgProto=dom.SVGElement&&dom.SVGElement.prototype,inner=null;
for(var at=svgProto;at&&!inner;at=Object.getPrototypeOf(at))inner=Object.getOwnPropertyDescriptor(at,'innerHTML')||null;
if(svgProto&&inner&&inner.set)try{Object.defineProperty(svgProto,'innerHTML',{configurable:true,get:inner.get,set:function(markup){
var parsed=null;
try{parsed=new dom.DOMParser().parseFromString('<svg xmlns="http://www.w3.org/2000/svg">'+String(markup)+'</svg>','image/svg+xml').documentElement;}catch(error){}
if(!parsed){inner.set.call(this,markup);return;}
while(this.firstChild)this.removeChild(this.firstChild);
var element=this;
[].slice.call(parsed.childNodes).forEach(function(node){element.appendChild(doc.importNode(node,true));});
}});}catch(error){}
// Shadow roots, open or closed, so the still can carry them (declarative shadow DOM).
var shadows=[],attach=elementProto&&elementProto.attachShadow;
if(attach)elementProto.attachShadow=function(options){var root=attach.call(this,options);shadows.push([this,root]);return root;};
// Pages read these on the document too.
var written=null,current=null,state='loading';
function define(name,descriptor){try{Object.defineProperty(doc,name,Object.assign({configurable:true},descriptor));}catch(error){}}
define('cookie',{get:function(){return '';},set:noop});
define('readyState',{get:function(){return state;}});
define('currentScript',{get:function(){return current;}});
define('defaultView',{get:function(){return self;}});
define('visibilityState',{get:function(){return 'visible';}});
define('hidden',{get:function(){return false;}});
define('write',{value:function(){if(written!==null)written+=[].join.call(arguments,'');}});
define('writeln',{value:function(){if(written!==null)written+=[].join.call(arguments,'')+'\\n';}});
// The page's scripts, in order; one that throws doesn't stop the next, as in a browser.
var scripts=[].slice.call(doc.querySelectorAll('script')),modules=[];
scripts.forEach(function(script){
var type=(script.getAttribute('type')||'').trim().toLowerCase();
if(script.hasAttribute('src'))return;
// Module scripts wait for the classic ones, as in a browser.
if(type==='module'){modules.push(script.textContent||'');return;}
if(type&&!/^(text|application)\\/(x-)?(java|ecma)script$/.test(type))return;
current=script;written='';
run(script.textContent||'');
if(written&&script.parentNode){
var holder=doc.createElement('template');holder.innerHTML=written;
script.parentNode.insertBefore(holder.content,script.nextSibling);
}
written=null;current=null;
});
runModules(modules,function(){
state='interactive';
try{doc.dispatchEvent(new dom.Event('DOMContentLoaded',{bubbles:true}));}catch(error){}
try{self.dispatchEvent(new NativeEvent('DOMContentLoaded'));}catch(error){}
state='complete';
try{self.dispatchEvent(new NativeEvent('load'));}catch(error){}
try{if(typeof self.onload==='function')self.onload(new NativeEvent('load'));}catch(error){}
var body=doc.body;
if(body&&body.getAttribute('onload'))run(body.getAttribute('onload'));
later(function(){
var still;
try{
[].slice.call(doc.querySelectorAll('script')).forEach(function(script){script.remove();});
// Innermost first, so a shadow root inside another goes into its host's template too.
shadows.slice().reverse().forEach(function(pair){
var host=pair[0],root=pair[1],template=doc.createElement('template');
template.setAttribute('shadowrootmode','open');
template.innerHTML=root.innerHTML;
host.insertBefore(template,host.firstChild);
});
still=doc.toString();
}catch(error){failed(error);send({html:null,reason:'error',errors:errors});return;}
// A page that grew past what a card shows keeps its markup: parsing it would stall the app.
if(still.length>max){send({html:null,reason:'large',errors:errors});return;}
send({html:still,reason:'done',errors:errors});
},settle);
});
}
function runModules(list,done){
if(!list.length){done();return;}
var code=list.shift();
// A module's own scope and top-level await, without importing anything: the worker never imports
// (Chromium lets an imported module's own imports past the frame's policy). import/export lines fail.
run("self.__konvoModule=(async function(){'use strict';\\n"+code+"\\n})();");
var running=self.__konvoModule;
delete self.__konvoModule;
if(running&&typeof running.then==='function')running.then(function(){runModules(list,done);},function(error){failed(error);runModules(list,done);});
else runModules(list,done);
}
})();
`;
}

/**
 * The hidden frame's page: it hands Konvo a private channel, makes the worker's script a blob once,
 * and for each page starts a fresh worker, stops it at the budget, and answers with the still or
 * null. Its own policy allows nothing but its script and the worker; the worker inherits it.
 */
export function snapshotHostDocument(nonce: string | undefined): string {
	const script = `(function(){
var channel=new MessageChannel(),port=channel.port1,url=null;
parent.postMessage({konvoSnapshots:1},'*',[channel.port2]);
port.onmessage=function(event){
var data=event.data||{};
if(typeof data.konvoSnapshotCode==='string'){if(!url)url=URL.createObjectURL(new Blob([data.konvoSnapshotCode],{type:'text/javascript'}));return;}
if(typeof data.id!=='number')return;
var done=false,worker=null,timer=null;
function finish(html,reason){
if(done)return;done=true;
if(timer)clearTimeout(timer);
try{if(worker)worker.terminate();}catch(error){}
port.postMessage({id:data.id,html:typeof html==='string'?html:null,reason:reason});
}
if(!url){finish(null,'unready');return;}
try{worker=new Worker(url);}catch(error){finish(null,'worker');return;}
timer=setTimeout(function(){finish(null,'timeout');},data.budget);
worker.onmessage=function(message){var reply=message.data||{};finish(typeof reply.html==='string'&&reply.html.length<=data.max?reply.html:null,reply.reason||'done');};
worker.onerror=function(error){if(error&&error.preventDefault)error.preventDefault();finish(null,'error');};
worker.postMessage({html:data.html,width:data.width,settle:data.settle,max:data.max});
};
})();`;
	const attribute = nonce && /^[A-Za-z0-9+/=_-]+$/.test(nonce) ? ` nonce="${nonce}"` : '';
	return `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="${SNAPSHOT_POLICY}"></head><body><script${attribute}>${script}</script></body></html>`;
}

interface Host {
	run: (html: string, width: number) => Promise<string | null>;
}

let host: Promise<Host> | null = null;

function startHost(): Promise<Host> {
	host ??= (async () => {
		const {default: linkedom} = await import('linkedom/worker?raw');
		const dom = classicDom(linkedom);
		if (!dom) throw new Error('linkedom changed shape');
		const frame = document.createElement('iframe');
		frame.setAttribute('sandbox', 'allow-scripts');
		// Chromium's workers don't take the frame's own policy (its meta tag), only this one.
		frame.setAttribute('csp', SNAPSHOT_POLICY);
		frame.setAttribute('aria-hidden', 'true');
		frame.setAttribute('referrerpolicy', 'no-referrer');
		frame.tabIndex = -1;
		frame.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden;pointer-events:none';
		frame.srcdoc = snapshotHostDocument(document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce);
		const port = await new Promise<MessagePort>((resolve, reject) => {
			const timer = setTimeout(() => {
				window.removeEventListener('message', onMessage);
				reject(new Error('The stills frame did not start'));
			}, HOST_TIMEOUT_MS);
			const onMessage = (event: MessageEvent) => {
				if (event.source !== frame.contentWindow || event.origin !== 'null') return;
				if ((event.data as {konvoSnapshots?: unknown} | null)?.konvoSnapshots !== 1 || !event.ports[0]) return;
				clearTimeout(timer);
				window.removeEventListener('message', onMessage);
				resolve(event.ports[0]);
			};
			window.addEventListener('message', onMessage);
			document.body.append(frame);
		});
		port.postMessage({konvoSnapshotCode: dom + snapshotRunner()});
		const waiting = new Map<number, (html: string | null) => void>();
		port.onmessage = (event) => {
			const {id, html} = (event.data ?? {}) as {id?: number; html?: unknown};
			if (typeof id !== 'number') return;
			waiting.get(id)?.(typeof html === 'string' && html.length <= SNAPSHOT_MAX_LENGTH ? html : null);
			waiting.delete(id);
		};
		let next = 0;
		// One page at a time: each has the worker's whole budget.
		let queue: Promise<unknown> = Promise.resolve();
		return {
			run: (html: string, width: number) => {
				const result = queue.then(
					() =>
						new Promise<string | null>((resolve) => {
							const id = ++next;
							const timer = setTimeout(() => {
								waiting.delete(id);
								resolve(null);
							}, SNAPSHOT_BUDGET_MS + HOST_TIMEOUT_MS);
							waiting.set(id, (still) => {
								clearTimeout(timer);
								resolve(still);
							});
							port.postMessage({
								id,
								html,
								width,
								budget: SNAPSHOT_BUDGET_MS,
								settle: SNAPSHOT_SETTLE_MS,
								max: SNAPSHOT_MAX_LENGTH,
							});
						}),
				);
				queue = result;
				return result;
			},
		};
	})().catch((error) => {
		host = null;
		throw error;
	});
	return host;
}

const stills = new Map<string, Promise<string | null>>();

/** A short, stable key for a page's text. */
function pageKey(html: string, width: number): string {
	let hash = 0x811c9dc5;
	for (let i = 0; i < html.length; i++) {
		hash ^= html.charCodeAt(i);
		hash = Math.imul(hash, 0x01000193);
	}
	return `${width}:${html.length}:${(hash >>> 0).toString(36)}`;
}

/** The page as its script leaves it, as static HTML; null when it can't be had in time. */
export function pageSnapshot(html: string, width: number): Promise<string | null> {
	const key = pageKey(html, width);
	const known = stills.get(key);
	if (known) {
		stills.delete(key);
		stills.set(key, known);
		return known;
	}
	const still = startHost()
		.then((frame) => frame.run(html, width))
		.catch(() => null);
	stills.set(key, still);
	while (stills.size > KEPT) stills.delete(stills.keys().next().value as string);
	return still;
}
